import {
  TIM_SKILL_DEFINITIONS,
  getTimSkillDefinition,
  isTimSkillId,
  type TimSkillId,
} from "./timSkillDefinitions";
import { compareHuge, type HugeNumber } from "@/shared/numbers";

export interface TimSkillSnapshot {
  readonly acquired: ReadonlySet<TimSkillId>;
}

export interface TimSkillState {
  readonly acquiredIds: readonly TimSkillId[];
}

export type TimSkillAcquireResult =
  | { readonly ok: true }
  | {
      readonly ok: false;
      readonly reason:
        | "already-acquired"
        | "prerequisites-not-met"
        | "insufficient-tim";
    };

export const emptyTimSkillState: TimSkillState = {
  acquiredIds: [],
};

export class TimSkillModel {
  private readonly acquired = new Set<TimSkillId>();

  isAcquired(id: TimSkillId): boolean {
    return this.acquired.has(id);
  }

  arePrerequisitesMet(id: TimSkillId): boolean {
    const def = getTimSkillDefinition(id);
    for (const req of def.prerequisites) {
      if (!this.acquired.has(req)) return false;
    }
    return true;
  }

  canAcquire(id: TimSkillId, tim: HugeNumber): boolean {
    if (this.isAcquired(id)) return false;
    if (!this.arePrerequisitesMet(id)) return false;
    return compareHuge(tim, getTimSkillDefinition(id).costTim) >= 0;
  }

  acquire(id: TimSkillId, tim: HugeNumber): TimSkillAcquireResult {
    if (this.isAcquired(id)) {
      return { ok: false, reason: "already-acquired" };
    }
    if (!this.arePrerequisitesMet(id)) {
      return { ok: false, reason: "prerequisites-not-met" };
    }
    if (compareHuge(tim, getTimSkillDefinition(id).costTim) < 0) {
      return { ok: false, reason: "insufficient-tim" };
    }
    this.acquired.add(id);
    return { ok: true };
  }

  getSnapshot(): TimSkillSnapshot {
    return { acquired: new Set(this.acquired) };
  }

  exportState(): TimSkillState {
    return {
      acquiredIds: [...this.acquired],
    };
  }

  restoreState(state: TimSkillState): void {
    this.acquired.clear();
    for (const id of state.acquiredIds) {
      if (!isTimSkillId(id)) {
        throw new Error(`unknown tim skill id: ${String(id)}`);
      }
      this.acquired.add(id);
    }
  }

  static listDefinitions() {
    return TIM_SKILL_DEFINITIONS;
  }
}
